import React, { Fragment, memo, MouseEvent, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Popover, Typography } from '@mui/material'
import { useAuth } from '@workos-inc/authkit-react'
import { LogOutMinor } from '@shopify/polaris-icons'
import { useSelector } from 'react-redux'
import { ActionButton } from 'src/components/shared/ActionButton'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { useSignOut } from 'src/hooks/useSignOut'
import { getInternalIdentity } from 'src/selectors/internalAuth'
import { buildUserDisplayName, truncateDisplayName } from 'src/utils/userDisplayName'

/**
 * The signed-in user in the top bar: who you are, what role you hold, and the way out.
 *
 * The name comes from WorkOS (the identity provider owns it) and the role from
 * `/internal/auth/me` (only GigSafe knows it). Whatever neither supplies is left out rather than
 * stood in for.
 *
 * A popover rather than the shared `ActionMenu`, whose items are all interactive — the identity
 * block is text, and rendering it as a disabled menu item would grey out the one thing the panel
 * exists to show. The button and paper keep the chrome the session-token control used, so the
 * bar's right-hand control is unchanged apart from what it opens.
 */

// Portal-rendered paper cannot be styled from the parent's css prop; mirrors the admin theme's
// menu paper.
const popoverPaperSx = {
  mt: 0.75,
  borderRadius: '8px',
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.07)',
}

const UserMenuButtonComponent = () => {
  const { user } = useAuth()
  const identity = useSelector(getInternalIdentity)
  const handleSignOut = useSignOut()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const displayName = buildUserDisplayName(user, identity)
  const email = user?.email ?? identity?.email

  return (
    <Fragment>
      <ActionButton
        color="white"
        size="small"
        className={tw`px-4`}
        selected={Boolean(anchorEl)}
        // Central icons paint via `currentColor`, so the colour is the text colour, not `fill`.
        icon={<GSIcon iconName="gs_user" size={20} css={tw`text-shades-1000`} />}
        text={truncateDisplayName(displayName)}
        onClick={handleOpen}
        dataTestId="admin-user-menu-button"
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: popoverPaperSx } }}
      >
        <div css={tw`w-[260px] p-4`} data-testid="admin-user-menu-panel">
          {displayName ? (
            <Typography variant="subtitle1" css={tw`font-medium text-shades-1000`}>
              {displayName}
            </Typography>
          ) : null}
          {/* Suppressed when the name already is the email — the fallback in
              `buildUserDisplayName` — so the panel never prints it twice. */}
          {email && email !== displayName ? (
            <Typography variant="body2" css={tw`mt-0.5 break-all text-shades-500`}>
              {email}
            </Typography>
          ) : null}
          {identity?.roleName ? (
            <Typography variant="body2" css={tw`mt-2 text-shades-700`}>
              Role: {identity.roleName}
            </Typography>
          ) : null}

          <div css={tw`mt-4 border-0 border-t border-solid border-shades-100 pt-3`}>
            <Button
              size="small"
              color="white"
              className={tw`w-full justify-start`}
              leftIcon={<LogOutMinor width={20} height={20} css={tw`rotate-180`} />}
              onClick={handleSignOut}
              data-testid="admin-user-menu-log-out"
            >
              Log out
            </Button>
          </div>
        </div>
      </Popover>
    </Fragment>
  )
}

export const UserMenuButton = memo(UserMenuButtonComponent)
